import {
  DisplayedPerfusionTerminationType,
  ClinicalItemLog,
  WithPerfusionTime,
} from '@features/components/ClinicalData/types';
import {
  BloodSampleClinicalDataSection,
  ClinicalDataNameValue,
  IdentityAccessTicketPayloadMin,
  IdentityAccessTicketUiMin,
  IdentityAccessTicketValue,
  IdentityUserDataMin,
  WithCommonMeasurements,
} from '@features/components/ClinicalData/components/ClinicalLog/LogItems/types';
import {
  radiometerNames,
  shortRadiometerNames,
  bloodSampleNames,
  pfRatioInSituNames,
  pfRatioOnOCSNames,
  perfusionTerminationNames,
  bloodFromNames,
} from '@features/components/ClinicalData/constants';
import { getFullName, toStringValue } from '@/utils/helpers';
import {
  BloodFromType,
  CaseLogEventType,
  CaseLogRecord,
  ClinicalItemType,
  Radiometer,
  RadiometerType,
} from '@/api/apis/case';

export const getNRPType = (nrpType?: string): string => {
  return nrpType ?? 'N/A';
};

export const getPerfusionTerminationType = (details?: string): string | null => {
  if (!details?.length) {
    return null;
  }

  return perfusionTerminationNames[details as unknown as DisplayedPerfusionTerminationType] ?? null;
};

export const getBloodSampleRadiometerName = (radiometerType: RadiometerType) => {
  return shortRadiometerNames[radiometerType as RadiometerType] || 'Custom';
};

export const getBloodSampleData = (
  radiometer?: Radiometer,
): WithCommonMeasurements<BloodSampleClinicalDataSection> | null => {
  if (!radiometer) {
    return null;
  }

  const { firstSection, secondSection, thirdSection } = radiometer;
  return {
    commonMeasurements: [{ name: bloodSampleNames.commonMeasurement, value: toStringValue(firstSection?.lac) }],
    data: {
      firstSection: firstSection
        ? [
            { name: bloodSampleNames.firstSection.temperature, value: toStringValue(firstSection.temperature) },
            { name: bloodSampleNames.firstSection.pH, value: toStringValue(firstSection.pH) },
            { name: bloodSampleNames.firstSection.pcO2, value: toStringValue(firstSection.pcO2) },
            { name: bloodSampleNames.firstSection.paO2, value: toStringValue(firstSection.paO2) },
            { name: bloodSampleNames.firstSection.bEecf, value: toStringValue(firstSection.bEecf) },
            { name: bloodSampleNames.firstSection.hcO3, value: toStringValue(firstSection.hcO3) },
            { name: bloodSampleNames.firstSection.tcO2, value: toStringValue(firstSection.tcO2) },
            { name: bloodSampleNames.firstSection.sO2, value: toStringValue(firstSection.sO2) },
          ]
        : [],
      secondSection: secondSection
        ? [
            { name: bloodSampleNames.secondSection.temperature, value: toStringValue(secondSection.temperature) },
            { name: bloodSampleNames.secondSection.pH, value: toStringValue(secondSection.pH) },
            { name: bloodSampleNames.secondSection.pcO2, value: toStringValue(secondSection.pcO2) },
            { name: bloodSampleNames.secondSection.pO2, value: toStringValue(secondSection.pO2) },
          ]
        : [],
      thirdSection: thirdSection
        ? [
            { name: bloodSampleNames.thirdSection.na, value: toStringValue(thirdSection.na) },
            { name: bloodSampleNames.thirdSection.k, value: toStringValue(thirdSection.k) },
            { name: bloodSampleNames.thirdSection.iCA, value: toStringValue(thirdSection.iCA) },
            { name: bloodSampleNames.thirdSection.glu, value: toStringValue(thirdSection.glu) },
            { name: bloodSampleNames.thirdSection.hct, value: toStringValue(thirdSection.hct) },
            { name: bloodSampleNames.thirdSection.hb, value: toStringValue(thirdSection.hb) },
          ]
        : [],
    },
  };
};

export const getPfRatioInSituData = (
  record: ClinicalItemLog<ClinicalItemType.PFRatioInSitu>,
  isTrialCase: boolean,
): WithCommonMeasurements<ClinicalDataNameValue[]> => {
  const {
    radiometerType,
    pfRatio,
    averagePFRatio,
    bloodFrom,
    peep,
    radiometer,
    vt,
    llpvpfRatio,
    lupvpfRatio,
    rupvpfRatio,
    rlpvpfRatio,
  } = record;

  const radiometerName = radiometerType ? radiometerNames[radiometerType] : 'Custom';
  const bloodFromName = bloodFrom ? bloodFromNames[bloodFrom as BloodFromType] : '';

  const pulmonaryVeinsMeasurements =
    bloodFrom === BloodFromType.PulmonaryVeins
      ? [
          { name: pfRatioInSituNames.averagePfRatio, value: toStringValue(averagePFRatio) },
          { name: pfRatioInSituNames.lupv, value: toStringValue(lupvpfRatio) },
          { name: pfRatioInSituNames.llpv, value: toStringValue(llpvpfRatio) },
          { name: pfRatioInSituNames.rupv, value: toStringValue(rupvpfRatio) },
          { name: pfRatioInSituNames.rlpv, value: toStringValue(rlpvpfRatio) },
        ]
      : [];

  if (isTrialCase) {
    return {
      commonMeasurements: [
        { name: pfRatioInSituNames.pfRatioOnOxygen, value: toStringValue(pfRatio || averagePFRatio) },
        { name: pfRatioInSituNames.paO2, value: toStringValue(radiometer?.firstSection.paO2) },
        { name: pfRatioInSituNames.pvO2, value: toStringValue(radiometer?.firstSection.pvO2) },
      ],
      data: [
        { name: pfRatioInSituNames.bloodFrom, value: toStringValue(bloodFromName) },
        { name: pfRatioInSituNames.radiometer, value: toStringValue(radiometerName) },
        { name: pfRatioInSituNames.peep, value: toStringValue(peep) },
        { name: pfRatioInSituNames.vt, value: toStringValue(vt) },
        ...pulmonaryVeinsMeasurements,
      ],
    };
  }

  return {
    commonMeasurements: [{ name: pfRatioInSituNames.pfRatioOnOxygen, value: toStringValue(pfRatio || averagePFRatio) }],
    data: [
      { name: pfRatioInSituNames.bloodFrom, value: toStringValue(bloodFromName) },
      { name: pfRatioInSituNames.radiometer, value: toStringValue(radiometerName) },
      { name: pfRatioInSituNames.paO2, value: toStringValue(radiometer?.firstSection.paO2) },
      { name: pfRatioInSituNames.peep, value: toStringValue(peep) },
      { name: pfRatioInSituNames.vt, value: toStringValue(vt) },
      ...pulmonaryVeinsMeasurements,
    ],
  };
};

export const getPfRatioOnOCSData = (
  record: ClinicalItemLog<ClinicalItemType.PFRatioOnOCSDonorHospital | ClinicalItemType.PFRatioOnOCSTXCenter>,
  isTrialCase: boolean,
): WithCommonMeasurements<ClinicalDataNameValue[]> => {
  const { pfRatio, radiometer, radiometerType, averagePFRatio } = record;

  if (isTrialCase) {
    return {
      commonMeasurements: [
        { name: pfRatioOnOCSNames.pfRatioOnOxygen, value: toStringValue(pfRatio || averagePFRatio) },
        { name: pfRatioOnOCSNames.radiometer, value: toStringValue(radiometerNames[radiometerType!]) },
        { name: pfRatioOnOCSNames.paO2, value: toStringValue(radiometer?.firstSection.paO2) },
        { name: pfRatioOnOCSNames.pvO2, value: toStringValue(radiometer?.firstSection.pvO2) },
      ],
      data: [],
    };
  }

  return {
    commonMeasurements: [{ name: pfRatioOnOCSNames.pfRatioOnOxygen, value: toStringValue(pfRatio || averagePFRatio) }],
    data: [
      { name: pfRatioOnOCSNames.radiometer, value: toStringValue(radiometerNames[radiometerType!]) },
      { name: pfRatioOnOCSNames.paO2, value: toStringValue(radiometer?.firstSection.paO2) },
    ],
  };
};

const safeParseJson = (raw?: unknown): unknown => {
  if (typeof raw !== 'string' || !raw) return undefined;
  try {
    return JSON.parse(raw);
  } catch {
    return undefined;
  }
};

const parseMaybeNestedJsonObject = <T extends object>(raw?: unknown): T | undefined => {
  if (raw && typeof raw === 'object') return raw as T;
  const parsed = safeParseJson(raw);
  return parsed && typeof parsed === 'object' ? (parsed as T) : undefined;
};

const normalizePhotoPath = (src?: string | null) => {
  const trimmed = (src ?? '').trim();
  return trimmed.length ? trimmed : undefined;
};

const getDurationLabel = (isInfinite?: boolean | null): '60-day' | 'indefinite' => {
  return isInfinite ? 'indefinite' : '60-day';
};

export const getIdentityAccessLogRecord = (
  eventType: CaseLogEventType,
  record: CaseLogRecord,
): IdentityAccessTicketUiMin | undefined => {
  const isGranted = eventType === CaseLogEventType.IdentityAccessTicketGranted;
  const isRevoked = eventType === CaseLogEventType.IdentityAccessTicketRevoked;
  if (!isGranted && !isRevoked) return undefined;

  const outer = safeParseJson(record.value);
  const payload = outer && typeof outer === 'object' ? (outer as IdentityAccessTicketPayloadMin) : undefined;
  if (!payload) return undefined;

  const forUser = parseMaybeNestedJsonObject<IdentityUserDataMin>(payload.forUserData);
  const fullName = getFullName(forUser?.firstName ?? null, forUser?.lastName ?? null);
  const fallbackId = forUser?.id ?? payload.forUserId ?? 'user';

  if (isRevoked) {
    return {
      textPrefix: 'Revoke access from',
      target: {
        id: forUser?.id ?? payload.forUserId ?? undefined,
        fullName: fullName || fallbackId,
        photoPath: normalizePhotoPath(forUser?.photoPath),
      },
    };
  }

  const durationLabel = getDurationLabel(payload.isInfinite);

  return {
    durationLabel,
    textPrefix: `Grant ${durationLabel} access to`,
    target: {
      id: forUser?.id ?? payload.forUserId ?? undefined,
      fullName: fullName || fallbackId,
      photoPath: normalizePhotoPath(forUser?.photoPath),
    },
  };
};

export const getIdentityAccessTicketTextFromClinicalLog = (record: WithPerfusionTime<ClinicalItemLog>) => {
  const parsed = safeParseJson(record.details);
  const ticket = parsed && typeof parsed === 'object' ? (parsed as IdentityAccessTicketValue) : undefined;

  const target = ticket?.forUserId ?? 'user';

  if (record.clinicalItemType === ClinicalItemType.IdentityAccessTicketRevoked) {
    return `Revoke access from ${target}`;
  }

  const duration = ticket?.isInfinite ? 'indefinite' : '60-day';
  return `Grant ${duration} access to ${target}`;
};
